import { SortDirection, USER_SORT_KEYS, UserSortKey } from "../../../constants/users";
import { UserV3 } from "../../../store/slices/UsersV3Slice";
import { AllMatchingFilters, UserListFilters, UserSelection } from "../../../services/UsersV3Service";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// Last Login column copy from the design: "40 mins ago", "2h ago", "3 days ago",
// then a short date ("Thu, 12/07") once it is a week or more old. The year
// is added for logins outside the current year so old dates are not ambiguous.
// Never logged in (or an unparseable date) renders as an em dash.
export const formatLastLogin = (isoDate: string | null | undefined, now: Date = new Date()): string => {
  if (!isoDate) return "—";
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return "—";

  // Clock skew can put a fresh login slightly in the future; treat it as now.
  const elapsed = Math.max(0, now.getTime() - date.getTime());
  if (elapsed < MINUTE) return "Just now";
  if (elapsed < HOUR) {
    const minutes = Math.floor(elapsed / MINUTE);
    return `${minutes} ${minutes === 1 ? "min" : "mins"} ago`;
  }
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h ago`;
  if (elapsed < 7 * DAY) {
    const days = Math.floor(elapsed / DAY);
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  }

  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  const monthDay = date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
  return date.getFullYear() === now.getFullYear()
    ? `${weekday}, ${monthDay}`
    : `${weekday}, ${monthDay}/${date.getFullYear()}`;
};

export type UserSort = { by: UserSortKey; dir: SortDirection } | null;

// Column header click: ascending → descending → unsorted. Clicking a different
// column starts it at ascending (only one column is sorted at a time).
export const nextSort = (current: UserSort, key: UserSortKey): UserSort => {
  if (!current || current.by !== key) return { by: key, dir: "asc" };
  if (current.dir === "asc") return { by: key, dir: "desc" };
  return null;
};

export const getUserDisplayName = (user: Pick<UserV3, "firstName" | "lastName" | "email">) =>
  [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.email;

// Row selection for the bulk actions. "ids" holds explicitly ticked users (kept
// across pages, with their org for the move confirmation copy); "allMatching" is
// "Select within all pages" — every user matching the filters, minus unticked rows.
type SelectedUserRef = Pick<UserV3, "id" | "organization">;
export type RowSelection =
  | { mode: "ids"; users: Record<string, SelectedUserRef> }
  | { mode: "allMatching"; excludedIds: string[] };

export const EMPTY_SELECTION: RowSelection = { mode: "ids", users: {} };

export const isRowSelected = (selection: RowSelection, userId: string) =>
  selection.mode === "ids"
    ? userId in selection.users
    : !selection.excludedIds.includes(userId);

export const toggleRow = (selection: RowSelection, user: UserV3): RowSelection => {
  if (selection.mode === "allMatching") {
    const excludedIds = selection.excludedIds.includes(user.id)
      ? selection.excludedIds.filter((id) => id !== user.id)
      : [...selection.excludedIds, user.id];
    return { mode: "allMatching", excludedIds };
  }
  const users = { ...selection.users };
  if (user.id in users) delete users[user.id];
  else users[user.id] = { id: user.id, organization: user.organization };
  return { mode: "ids", users };
};

// Header checkbox: select or clear every row on the current page only.
export const setPageSelected = (
  selection: RowSelection,
  pageUsers: UserV3[],
  selected: boolean
): RowSelection => {
  const pageIds = pageUsers.map((user) => user.id);
  if (selection.mode === "allMatching") {
    const excludedIds = selected
      ? selection.excludedIds.filter((id) => !pageIds.includes(id))
      : Array.from(new Set([...selection.excludedIds, ...pageIds]));
    return { mode: "allMatching", excludedIds };
  }
  const users = { ...selection.users };
  pageUsers.forEach((user) => {
    if (selected) users[user.id] = { id: user.id, organization: user.organization };
    else delete users[user.id];
  });
  return { mode: "ids", users };
};

export const getSelectedCount = (selection: RowSelection, totalItems: number) =>
  selection.mode === "ids"
    ? Object.keys(selection.users).length
    : Math.max(0, totalItems - selection.excludedIds.length);

// Source org names for the move confirmation ("from {Org A}"); null when the
// rows span several orgs or the selection is "all pages" (unknown client-side).
export const getSingleSourceOrganization = (selection: RowSelection) => {
  if (selection.mode !== "ids") return null;
  const orgs = Object.values(selection.users).map((user) => user.organization);
  const first = orgs[0];
  if (!first || orgs.some((org) => org?.id !== first.id)) return null;
  return first;
};

// Request body `selection` for the bulk endpoints. "All pages" is sent as the
// filters themselves so the server resolves the set at execution time. Unset
// filters are left out: the keys are optional and a null organizationId is a 400.
export const toApiSelection = (selection: RowSelection, filters: UserListFilters): UserSelection => {
  if (selection.mode === "ids") return { userIds: Object.keys(selection.users) };

  const allMatching: AllMatchingFilters = {};
  if (filters.role.length > 0) allMatching.role = filters.role;
  if (filters.status.length > 0) allMatching.status = filters.status;
  if (filters.organizationId != null) allMatching.organizationId = filters.organizationId;
  if (filters.search) allMatching.search = filters.search;
  return selection.excludedIds.length > 0
    ? { allMatching, excludeUserIds: selection.excludedIds }
    : { allMatching };
};

// Ends a sentence after a name without doubling a period the name already has
// (e.g. "Gulshan Inc." → "...to Gulshan Inc." rather than "Gulshan Inc..").
export const endSentence = (text: string) => (text.endsWith(".") ? text : `${text}.`);

export const pluralizeUsers = (count: number) => `${count.toLocaleString("en-US")} ${count === 1 ? "user" : "users"}`;

export interface UserListQuery {
  filters: UserListFilters;
  sort: UserSort;
  page: number;
}

export const parseUserListQuery = (params: URLSearchParams): UserListQuery => {
  const organizationId = Number(params.get("org"));
  const sortBy = params.get("sortBy") as UserSortKey | null;
  const sortDir = params.get("sortDir");
  const page = Number(params.get("page"));
  return {
    filters: {
      role: params.getAll("role"),
      status: params.getAll("status"),
      organizationId: Number.isInteger(organizationId) && organizationId > 0 ? organizationId : null,
      search: (params.get("q") ?? "").trim(),
    },
    sort:
      sortBy && USER_SORT_KEYS.includes(sortBy) && (sortDir === "asc" || sortDir === "desc")
        ? { by: sortBy, dir: sortDir }
        : null,
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
};

export const toUserListSearchParams = ({ filters, sort, page }: UserListQuery): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.search) params.set("q", filters.search);
  filters.role.forEach((role) => params.append("role", role));
  filters.status.forEach((status) => params.append("status", status));
  if (filters.organizationId != null) params.set("org", String(filters.organizationId));
  if (sort) {
    params.set("sortBy", sort.by);
    params.set("sortDir", sort.dir);
  }
  if (page > 1) params.set("page", String(page));
  return params;
};
